"use client";

import {useState} from "react";
import {useSWRConfig} from "swr";
import {useApiEndpoint} from "@/hooks/use-api-endpoint";
import {apiClient} from "@/lib/api-client";
import {DisclaimerData} from "@/lib/types";

/**
 * Hook to manage the AI disclaimer state.
 * Fetches current acknowledgment status and provides a method to acknowledge.
 *
 * @returns Disclaimer status, content, loading state, and acknowledge function
 */
export function useDisclaimer() {
  const {mutate} = useSWRConfig();
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  const {data, error, isLoading} = useApiEndpoint<DisclaimerData>(
    "/api/disclaimer"
  );

  const acknowledge = async () => {
    setIsAcknowledging(true);
    try {
      await apiClient.fetch("/api/disclaimer", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          disclaimerAccept: true,
          acceptedAt: new Date().toISOString(),
        }),
      });
      // Revalidate the disclaimer status
      await mutate("/api/disclaimer");
    } finally {
      setIsAcknowledging(false);
    }
  };

  return {
    isAcknowledged: data?.isAcknowledged ?? false,
    acknowledgedAt: data?.acknowledgedAt ?? null,
    currentVersion: data?.currentVersion ?? null,
    content: data?.content ?? null,
    isLoading,
    isAcknowledging,
    error,
    acknowledge,
  };
}

